import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function Terms() {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  const updated = 'February 2026';

  return (
    <div className="min-h-screen" style={{ background: '#FAFAFA', fontFamily: 'Manrope, sans-serif' }}>
      <header className="border-b border-gray-200 bg-white sticky top-0 z-40">
        <div className="max-w-5xl mx-auto px-6 h-14 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-1" data-testid="terms-home-link">
            <span style={{ fontFamily: 'Bebas Neue', fontSize: '1.3rem', color: '#0F0F0F' }}>HUSCLE</span>
            <span style={{ fontFamily: 'Bebas Neue', fontSize: '1.3rem', color: '#FF2E00' }}>OS</span>
          </Link>
          <Link to="/" className="text-xs font-bold tracking-widest uppercase text-gray-500 hover:text-gray-900">Back to HuscleOS</Link>
        </div>
      </header>
      <div className="max-w-3xl mx-auto px-6 pt-16 pb-20">
        <p className="text-[#FF2E00] text-xs font-bold uppercase tracking-widest mb-3">Legal</p>
        <h1 className="text-4xl sm:text-5xl tracking-tight mb-2" style={{ fontFamily: 'Bebas Neue', color: '#0F0F0F' }}>
          TERMS OF SERVICE
        </h1>
        <p className="text-gray-500 text-sm mb-10">Last updated: {updated}</p>

        <div className="space-y-8 text-gray-700 text-sm leading-relaxed">
          <section>
            <h2 className="font-bold text-lg mb-3 text-gray-900">1. Acceptance of Terms</h2>
            <p>
              By accessing or using HuscleOS (the "Service", "Platform", or "Software") at huscleos.com or any subdomain,
              or by signing up for a HuscleOS subscription, you agree to be bound by these Terms of Service.
              If you do not agree, please do not use HuscleOS.
            </p>
          </section>

          <section>
            <h2 className="font-bold text-lg mb-3 text-gray-900">2. The Service</h2>
            <p>
              HuscleOS is software-as-a-service for gym operators. It includes a customer relationship manager (CRM),
              lead capture, automated follow-up (email and SMS), campaign tools, a custom website, event management,
              a blog/SEO engine, review request automation, and team account management.
            </p>
          </section>

          <section>
            <h2 className="font-bold text-lg mb-3 text-gray-900">3. Subscription Plans &amp; Billing</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>HuscleOS offers three plans: <strong>Launch ($149/mo)</strong>, <strong>Grow ($299/mo)</strong>, and <strong>Dominate ($599/mo)</strong>, each with a one-time launch setup fee.</li>
              <li>Subscriptions renew automatically at the end of each billing period unless cancelled.</li>
              <li>Setup fees are billed at onboarding and are non-refundable once work has begun.</li>
              <li>You may cancel at any time from your billing portal; your subscription will remain active until the end of the current billing cycle.</li>
              <li>Taxes, where applicable, are added at checkout.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-bold text-lg mb-3 text-gray-900">4. Your Account &amp; Data</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>You are responsible for keeping your login credentials secure and for all activity under your account.</li>
              <li>You retain ownership of all data you import or create in HuscleOS (member lists, lead data, campaigns, content).</li>
              <li>You may export your data at any time via CSV export, API, or by request.</li>
              <li>We will retain your data for 60 days after cancellation before permanent deletion.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-bold text-lg mb-3 text-gray-900">5. Acceptable Use</h2>
            <p className="mb-3">You agree not to use HuscleOS to:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Send unsolicited spam email or SMS (CAN-SPAM and TCPA compliance is mandatory).</li>
              <li>Host, distribute, or promote unlawful, harmful, or infringing content.</li>
              <li>Attempt to reverse-engineer, decompile, or probe the platform for vulnerabilities.</li>
              <li>Resell or white-label HuscleOS without a signed white-label agreement.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-bold text-lg mb-3 text-gray-900">6. SMS &amp; Email Communications</h2>
            <p>
              When you send SMS or email from HuscleOS, you are the sender of record. You are responsible for obtaining
              proper consent from your recipients, honoring unsubscribe/STOP requests, and complying with all applicable
              laws (CAN-SPAM, TCPA, CASL, GDPR). HuscleOS provides opt-out automation, but the legal responsibility is yours.
            </p>
          </section>

          <section>
            <h2 className="font-bold text-lg mb-3 text-gray-900">7. Uptime &amp; Support</h2>
            <p>
              We target 99.9% uptime and provide email support during business hours. Response SLAs vary by plan (Dominate includes priority support).
              We do not guarantee uninterrupted service — scheduled maintenance, third-party outages (Stripe, Twilio, Resend, hosting), and force-majeure events may cause downtime.
            </p>
          </section>

          <section>
            <h2 className="font-bold text-lg mb-3 text-gray-900">8. Intellectual Property</h2>
            <p>
              All HuscleOS software, templates, designs, documentation, and branding are the property of HuscleOS and protected by copyright and trademark law.
              You receive a non-exclusive, revocable license to use HuscleOS during your active subscription.
            </p>
          </section>

          <section>
            <h2 className="font-bold text-lg mb-3 text-gray-900">9. Limitation of Liability</h2>
            <p>
              HuscleOS is provided "as is." To the maximum extent permitted by law, HuscleOS and its team are not liable for indirect, incidental, consequential,
              or punitive damages — including lost profits, lost leads, lost data, or business interruption — arising from your use of the platform.
              Our total liability is capped at the fees you have paid us in the 12 months preceding a claim.
            </p>
          </section>

          <section>
            <h2 className="font-bold text-lg mb-3 text-gray-900">10. Termination</h2>
            <p>
              You may cancel at any time. We may suspend or terminate your account for unpaid invoices, violation of these Terms, or fraudulent activity.
              Upon termination, your access to the platform ends and your data enters the 60-day retention window before deletion.
            </p>
          </section>

          <section>
            <h2 className="font-bold text-lg mb-3 text-gray-900">11. Changes to These Terms</h2>
            <p>
              We may update these Terms from time to time. Material changes will be emailed to account owners with at least 14 days' notice before they take effect.
            </p>
          </section>

          <section>
            <h2 className="font-bold text-lg mb-3 text-gray-900">12. Governing Law</h2>
            <p>
              These Terms are governed by the laws of the State of California, USA. Any disputes will be resolved in the courts of Santa Cruz County, California.
            </p>
          </section>

          <section>
            <h2 className="font-bold text-lg mb-3 text-gray-900">13. Contact</h2>
            <p>
              Questions about these Terms? Email us at <a href="mailto:hustle@huscleos.com" className="text-[#FF2E00] font-semibold hover:underline">hustle@huscleos.com</a>.
            </p>
            <p className="mt-4">
              <strong className="text-gray-900">HuscleOS</strong>
            </p>
          </section>
        </div>
      </div>
      <footer className="border-t border-gray-200 bg-white py-8">
        <div className="max-w-5xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-gray-500">
          <p>© {new Date().getFullYear()} HuscleOS. All rights reserved.</p>
          <div className="flex items-center gap-6">
            <Link to="/privacy" className="hover:text-gray-900">Privacy</Link>
            <Link to="/terms" className="hover:text-gray-900">Terms</Link>
            <Link to="/" className="hover:text-gray-900">Home</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
